import type { ProjectBreakdownColumn } from "../types/DashboardType";

// The one definition of the Project Breakdown columns, shared by the table, its Columns menu and the Excel export.

export interface ProjectBreakdownColumnGroup {
  key: string;
  label: string;
  prefix?: string;
  keys?: string[];
}

export const getProjectBreakdownColumns = (emissionUnit: string): ProjectBreakdownColumn[] => [
  { key: "name", label: "Name", colSpan: 2, align: "left" },
  { key: "model", label: "Model", colSpan: 1, align: "center" },
  { key: "elements", label: "Type Elements", subLabel: "(count)", align: "center" },
  { key: "quantity", label: "Quantity", align: "center" },
  { key: "unit", label: "Unit", align: "center" },
  { key: "lifetime", label: "Lifetime", subLabel: "(years)", align: "center" },
  { key: "lifetimeOffset", label: "Lifetime offset", subLabel: "(years)", align: "center" },
  { key: "emission", label: "Emission", subLabel: `(${emissionUnit})`, colSpan: 1, align: "right" },
  { key: "isReused", label: "Reused", align: "center" },
  { key: "weight", label: "Weight", align: "center" },
  { key: "declarationNumber", label: "Declaration number", align: "center" },
  { key: "numberOfReplacements", label: "Number of replacements", align: "center" },
  { key: "emissionByStage.A1A3", label: "A1-A3", align: "center" },
  { key: "emissionByStage.A4", label: "A4", align: "center" },
  { key: "emissionByStage.A5", label: "A5", align: "center" },
  { key: "emissionByStage.B1", label: "B1", align: "center" },
  { key: "emissionByStage.B2", label: "B2", align: "center" },
  { key: "emissionByStage.B3", label: "B3", align: "center" },
  { key: "emissionByStage.B4", label: "B4", align: "center" },
  { key: "emissionByStage.B5", label: "B5", align: "center" },
  { key: "emissionByStage.B6", label: "B6", align: "center" },
  { key: "emissionByStage.B7", label: "B7", align: "center" },
  { key: "emissionByStage.C1", label: "C1", align: "center" },
  { key: "emissionByStage.C2", label: "C2", align: "center" },
  { key: "emissionByStage.C3", label: "C3", align: "center" },
  { key: "emissionByStage.C4", label: "C4", align: "center" },
  { key: "emissionByStage.D", label: "D", align: "center" },
  { key: "epd.link", label: "EPD Link", align: "center" },
  { key: "epd.standard", label: "EPD Standard", align: "center" },
  { key: "epd.type", label: "EPD Type", align: "center" },
  { key: "epd.validTo", label: "EPD Valid To", align: "center" },
];

export const PROJECT_BREAKDOWN_COLUMN_GROUPS: readonly ProjectBreakdownColumnGroup[] = [
  { key: "quantity", label: "Quantity", keys: ["quantity", "unit", "weight"] },
  { key: "lifetime", label: "Lifetime", keys: ["lifetime", "lifetimeOffset", "numberOfReplacements"] },
  { key: "material", label: "Material Properties", keys: ["isReused", "declarationNumber"] },
  { key: "emissionByStage", label: "Emission by Stage", prefix: "emissionByStage." },
  { key: "epd", label: "EPD", prefix: "epd." },
];

export const PROJECT_BREAKDOWN_DEFAULT_DISPLAY_ORDER: readonly string[] = [
  "model",
  "elements",
  "emission",
  "group:quantity",
  "group:lifetime",
  "group:material",
  "group:emissionByStage",
  "group:epd",
];

const HIDDEN_BY_DEFAULT_KEYS = ["declarationNumber", "isReused"];
const HIDDEN_BY_DEFAULT_PREFIXES = ["emissionByStage.", "epd."];

export const isColumnHiddenByDefault = (columnKey: string): boolean =>
  HIDDEN_BY_DEFAULT_KEYS.includes(columnKey) || HIDDEN_BY_DEFAULT_PREFIXES.some((prefix) => columnKey.startsWith(prefix));

export const projectBreakdownStorageKeys = (projectId: string) => ({
  columnVisibility: `gaiup-${projectId}-projectBreakdown.columnVisibility`,
  displayOrder: `gaiup-${projectId}-projectBreakdown.displayOrder`,
  collapsedGroups: `gaiup-${projectId}-projectBreakdown.collapsedGroups`,
});

export const findColumnGroup = (groupKey: string): ProjectBreakdownColumnGroup | undefined =>
  PROJECT_BREAKDOWN_COLUMN_GROUPS.find((group) => group.key === groupKey);

// Members of a group, in the order they appear in `columns`; the Name column never belongs to a group.
export const getColumnGroupColumns = (groupKey: string, columns: ProjectBreakdownColumn[]): ProjectBreakdownColumn[] => {
  const group = findColumnGroup(groupKey);
  if (!group) return [];
  const draggableColumns = columns.filter((column) => column.key !== "name");
  if (group.prefix) return draggableColumns.filter((column) => column.key.startsWith(group.prefix!));
  if (group.keys) return draggableColumns.filter((column) => group.keys!.includes(column.key));
  return [];
};

// A saved order keeps its sequence; default items it lacks (a group added since) are appended.
export const mergeDisplayOrder = (savedDisplayOrder: string[]): string[] => {
  if (savedDisplayOrder.length === 0) return [...PROJECT_BREAKDOWN_DEFAULT_DISPLAY_ORDER];
  const savedSet = new Set(savedDisplayOrder);
  const newItems = PROJECT_BREAKDOWN_DEFAULT_DISPLAY_ORDER.filter((item) => !savedSet.has(item));
  return [...savedDisplayOrder, ...newItems];
};

export const resolveColumnVisibility = (
  columns: ProjectBreakdownColumn[],
  savedColumnVisibility: Record<string, boolean>
): Record<string, boolean> => {
  const visibility: Record<string, boolean> = {};
  for (const column of columns) {
    visibility[column.key] = savedColumnVisibility[column.key] ?? !isColumnHiddenByDefault(column.key);
  }
  return visibility;
};

// The columns the table shows: Name pinned first, then the visible columns in display order.
export const orderVisibleColumns = (
  columns: ProjectBreakdownColumn[],
  displayOrder: string[],
  columnVisibility: Record<string, boolean>
): ProjectBreakdownColumn[] => {
  const orderedColumns: ProjectBreakdownColumn[] = [];
  const nameColumn = columns.find((column) => column.key === "name");
  if (nameColumn) orderedColumns.push(nameColumn);

  for (const orderId of displayOrder) {
    if (orderId.startsWith("group:")) {
      const groupColumns = getColumnGroupColumns(orderId.replace("group:", ""), columns);
      orderedColumns.push(...groupColumns.filter((column) => columnVisibility[column.key] !== false));
    } else {
      const column = columns.find((candidate) => candidate.key === orderId && candidate.key !== "name");
      if (column && columnVisibility[column.key] !== false) orderedColumns.push(column);
    }
  }

  return orderedColumns;
};

const readStoredJson = (storage: Storage, key: string): unknown => {
  try {
    const stored = storage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    // corrupted data — fall back to the defaults
    return null;
  }
};

const isVisibilityRecord = (value: unknown): value is Record<string, boolean> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

// Reads the project's saved Columns settings the way ColumnMenu does and returns what the table shows.
export const resolveVisibleColumns = (
  projectId: string | null | undefined,
  emissionUnit: string,
  storage: Storage | undefined = typeof localStorage === "undefined" ? undefined : localStorage
): ProjectBreakdownColumn[] => {
  const columns = getProjectBreakdownColumns(emissionUnit);
  let savedColumnVisibility: Record<string, boolean> = {};
  let savedDisplayOrder: string[] = [];

  if (projectId && storage) {
    const keys = projectBreakdownStorageKeys(projectId);
    const storedVisibility = readStoredJson(storage, keys.columnVisibility);
    const storedOrder = readStoredJson(storage, keys.displayOrder);
    if (isVisibilityRecord(storedVisibility)) savedColumnVisibility = storedVisibility;
    if (isStringArray(storedOrder)) savedDisplayOrder = storedOrder;
  }

  return orderVisibleColumns(
    columns,
    mergeDisplayOrder(savedDisplayOrder),
    resolveColumnVisibility(columns, savedColumnVisibility)
  );
};
